import { LocalDate, LocalDateTime } from "@js-joda/core";
import { match } from "ts-pattern";
import { apiErrors } from "../../../../scripts/consts/apiErrors.const";
import { dateUtils } from "../../../../scripts/consts/dateUtils";
import languages from "../../../../scripts/consts/languages";
import { loadable, Loadable } from "../../../../scripts/consts/loadable.const";
import {
  ComplianceAiReviewManualCheck,
  ComplianceAiReviewManualCheckCaregiver,
  CompliancePriorityPendingDocument,
  ComplianceV2Field,
  ComplianceV2FieldInstance,
  ComplianceV2FieldInstanceWithValue,
} from "../../../../scripts/messages/compliance";
import {
  CaregiverDocumentTypeId,
  CaregiverDocumentUploadId,
  CaregiverId,
  ComplianceV2ItemFieldPossibleValueId,
} from "../../../../scripts/messages/ids";
import { DatabaseApiService } from "../../../../scripts/services/db";
import { MfModalFactory } from "../../../../scripts/services/mfModalFactory";
import { arrayAt, assertDefined, fmap } from "../../../../scripts/utils/generalUtils";
import { CaregiverService } from "../../../caregiver/services/caregiver.service";
import { MultiselectOption } from "../../../shared/components/editable-label/editable-label.component";
import { CompService } from "../../comp.service";
import "./ai-review-manual-check.component.scss";

interface ActiveDocumentState {
  isCompliant: boolean;
  effectiveDate: Date;
  expiryDate: Date | null;
  fields: WithNgModel<ComplianceV2FieldInstance>[];
  expires: boolean;
  requireReVerification: boolean;
  files: null;
  expiryDateButtons: {
    title: string;
    active: boolean;
    date: Date;
  }[];
  ignoreFiles: boolean;
  copyFromCaregiverDocumentUploadId?: CaregiverDocumentUploadId;
}

interface SwapDocumentType {
  model: {
    id: CaregiverDocumentTypeId | null;
  }[];
  isTrue: boolean;
}

interface ComponentOptions extends angular.IComponentOptions {
  $name: string;
}

type WithNgModel<$Field extends ComplianceV2Field> = $Field extends ComplianceV2Field.Date
  ? $Field & { model: { value: Date | null } }
  : $Field & { model: string | null };

//! @ngInject
class Controller implements ng.IComponentController {
  static readonly $name = "AiReviewManualCheckScreen";

  collapseState: Map<CaregiverId, boolean> = new Map();
  collapseStateDocuments: Map<CaregiverDocumentUploadId, boolean> = new Map();

  isLoading = false;

  aiReviewManualCheck: Loadable<ComplianceAiReviewManualCheck["caregiverDocuments"]> =
    loadable.loading();

  documentsState: Map<
    CaregiverDocumentUploadId,
    Map<CaregiverDocumentTypeId, ActiveDocumentState>
  > = new Map();
  dropdownDocumentOptions: { id: CaregiverDocumentTypeId; label: string }[] = [];
  documentTypeIdToName: Map<CaregiverDocumentTypeId, string> = new Map();
  swapDocumentType: Map<CaregiverDocumentUploadId, SwapDocumentType> = new Map();

  resetViewState = () => {
    this.collapseState = new Map();
    this.collapseStateDocuments = new Map();
    this.isEditMode = false;
    this.documentTypeIdToName = new Map();
    this.documentsState = new Map();
    this.swapDocumentType = new Map();
  };

  searchExtraOptions = {
    displayProp: "label",
    styleActive: true,
    scrollable: true,
    scrollableHeight: "300px",
    enableSearch: true,
  };

  searchParams: {
    from: Date | null;
    to: Date | null;
    statuses: { id: string; label: string }[];
    reviewStatuses: { id: string; label: string }[];
    documents: { id: CaregiverDocumentTypeId; label: string }[];
  } = {
    from: new Date(),
    to: new Date(),
    statuses: [],
    reviewStatuses: [],
    documents: [],
  };

  statusOptions = [
    {
      id: "Pending",
      label: "Pending",
    },
    {
      id: "Approved",
      label: "Approved",
    },
    {
      id: "Rejected",
      label: "Rejected",
    },
  ];

  reviewStatusOptions = [
    {
      id: "approved",
      label: "Approved",
    },
    {
      id: "senior_human_review_required",
      label: "Senior human review required",
    },
    {
      id: "rejected",
      label: "Rejected",
    },
    {
      id: "not_compliant",
      label: "Not Compliant",
    },
    {
      id: "human_review_required",
      label: "Human review required",
    },
  ];

  documentOptions: { id: CaregiverDocumentTypeId; label: string }[] = [];

  isEditMode = false;
  agencyCertificationsOptions?: MultiselectOption[];
  languagesOptions = languages.map((language) => ({
    id: language.value,
    label: language.text,
  }));
  genderOptions = [
    { id: "M", label: "Male" },
    { id: "F", label: "Female" },
  ];

  constructor(
    private mfModal: MfModalFactory,
    private compService: CompService,
    private $uibModal: ng.ui.bootstrap.IModalService,
    private $rootScope: angular.IRootScopeService,
    private DatabaseApi: DatabaseApiService,
    private caregiverService: CaregiverService
  ) {}

  $onInit(): void {
    this.fetchAgencyDocumentTypes();
    this.fetchAgencyCertifications();
    this.$rootScope.$on("got_agency_certifications", () => this.fetchAgencyCertifications());
  }

  createUpdateCaregiverFieldFunction = (id: CaregiverId) => {
    return (field: string, value: unknown) => {
      this.updateCaregiverField(id, field, value);
    };
  };

  updateCaregiverField = (id: CaregiverId, field: string, value: unknown) => {
    if (!loadable.isResolved(this.aiReviewManualCheck)) {
      console.error("data is not resolved");
      return;
    }

    this.caregiverService
      .updateField(id, field, value)
      .then(() => {
        this.promptSuccessModal("Caregiver updated successfully");
        // this.fetchData();
      })
      .catch((error) => {
        console.error(error);
        this.promptErrorModal(error);
      });
  };

  promptReject(
    caregiver: ComplianceAiReviewManualCheckCaregiver,
    document: CompliancePriorityPendingDocument
  ) {
    const modal = this.mfModal.create({
      variant: "warning",
      subject: "Reject document",
      showInput: true,
      inputIsRequired: false,
      inputType: "text",
      inputPlaceholder: "Enter a reason for rejecting this document",
      message:
        "Are you sure you want to reject this document? The caregiver will be notified and will be able to upload a new document.",
      onConfirm: async ({ inputModel }) => {
        if (!loadable.isResolved(this.aiReviewManualCheck)) {
          console.error("Documents not resolved");
          return;
        }

        modal.setLoading(true);

        this.compService
          .rejectDocument({
            caregiverId: caregiver.id,
            uploadedDocumentId: document.caregiverDocumentUploadId,
            reason: inputModel ?? null,
          })
          // .then(() => this.fetchData())
          .catch((error) => this.promptErrorModal(error))
          .finally(() => modal.close());
      },
    });
  }

  promptApprove(
    caregiver: ComplianceAiReviewManualCheckCaregiver,
    document: CompliancePriorityPendingDocument
  ) {
    const modal = this.mfModal.create({
      subject: "Approve document",
      message: "Are you sure you want to approve this document?",
      onConfirm: async () => {
        const { documentTypeId, activeDocumentState, wasSwapped } = (() => {
          const state = assertDefined(
            this.documentsState
              .get(document.caregiverDocumentUploadId)
              ?.get(document.caregiverDocumentTypeId),
            "activeDocumentState"
          );

          const swap = assertDefined(
            this.swapDocumentType.get(document.caregiverDocumentUploadId),
            "swapDocumentType"
          );

          if (swap.isTrue) {
            const newDocumentTypeId = arrayAt(swap.model, 0)?.id ?? null;

            if (newDocumentTypeId === null) {
              throw new Error("Pleae select a document type");
            }

            return {
              documentTypeId: newDocumentTypeId,
              activeDocumentState: assertDefined(
                this.documentsState.get(document.caregiverDocumentUploadId)?.get(newDocumentTypeId),
                "activeDocumentState (newDocumentTypeId)"
              ),
              wasSwapped: true,
            };
          }

          return {
            documentTypeId: document.caregiverDocumentTypeId,
            activeDocumentState: state,
            wasSwapped: false,
          };
        })();

        modal.setLoading(true);

        this.compService
          .createComplianceInstance({
            caregiverId: caregiver.id,
            data: {
              caregiverDocumentTypeId: documentTypeId,
              effectiveDate: dateUtils.dateToLocalDate(activeDocumentState.effectiveDate),
              isCompliant: activeDocumentState.isCompliant,
              caregiverDocumentUploadId: document.caregiverDocumentUploadId,
              shouldDuplicateUploadForFollowup: true,
              expiryDate:
                fmap(activeDocumentState.expiryDate, dateUtils.dateToLocalDate) ?? undefined,
              duplicateDocumentUpload: activeDocumentState.copyFromCaregiverDocumentUploadId,
              swapUploadedDocumentTypeId: wasSwapped,
              fields: activeDocumentState.fields.map((field) =>
                match(field)
                  .with(
                    { type: "Text" },
                    (field): ComplianceV2FieldInstanceWithValue => ({
                      id: field.id,
                      type: field.type,
                      value: field.model,
                      isMandatory: field.isMandatory,
                      name: field.name,
                    })
                  )
                  .with(
                    { type: "Dropdown" },
                    (field): ComplianceV2FieldInstanceWithValue => ({
                      id: field.id,
                      type: field.type,
                      value: fmap(
                        field.model,
                        (idAsStr) =>
                          field.possibleValues.find(({ id }) => {
                            return (
                              id === ComplianceV2ItemFieldPossibleValueId.wrap(parseInt(idAsStr))
                            );
                          }) ?? null
                      ),
                      isMandatory: field.isMandatory,
                      name: field.name,
                      possibleValues: field.possibleValues,
                    })
                  )
                  .with(
                    { type: "Date" },
                    (field): ComplianceV2FieldInstanceWithValue => ({
                      id: field.id,
                      type: field.type,
                      value: fmap(field.model.value, dateUtils.dateToLocalDate),
                      isMandatory: field.isMandatory,
                      name: field.name,
                    })
                  )
                  .exhaustive()
              ),
            },
          })
          .then(() => {
            this.swapDocumentType.set(document.caregiverDocumentUploadId, {
              isTrue: false,
              model: [],
            });

            // this.fetchData();
          })
          .then(() => modal.close())
          .catch((error) => {
            modal.close();
            this.promptErrorModal(error);
          });
      },
    });
  }

  promptErrorModal(error: Error) {
    const modal = this.mfModal.create({
      variant: "danger",
      subject: "Error",
      message: apiErrors.format(error, "Something went wrong"),
      confirmLabel: "Close",
      onConfirm: () => modal.close(),
      hideCancelButton: true,
    });
  }

  promptSuccessModal(message: string) {
    const modal = this.mfModal.create({
      subject: "Success!",
      message: message,
      confirmLabel: "Close",
      hideCancelButton: true,
      onConfirm: () => modal.close(),
    });
  }

  promptCaregiverStatusChangeModal(
    caregiver: ComplianceAiReviewManualCheckCaregiver,
    showCompliantMessage: boolean
  ) {
    this.$uibModal
      .open({
        templateUrl: "admin/views/edit-caregiver-status-modal.html",
        size: "md",
        controller: "editCaregiverStatusModalCtrl",
        windowClass: "center-center",
        resolve: {
          caregiver: caregiver,
          isFromPrioritizedCaregeiversScreen: showCompliantMessage,
        },
      })
  }

  formatLocalDateTime = (localDateTime: LocalDateTime) =>
    dateUtils.localDateTimeToMDYHMString(localDateTime);

  private async fetchData() {
    this.isLoading = true;
    return this.compService
      .aiReviewManualCheck({
        reviewStatuses:
          this.searchParams.reviewStatuses.length > 0
            ? this.searchParams.reviewStatuses.map((status) => status.id)
            : null,
        statuses:
          this.searchParams.statuses.length > 0
            ? this.searchParams.statuses.map((status) => status.id)
            : null,
        documents:
          this.searchParams.documents.length > 0
            ? this.searchParams.documents.map((document) => document.id)
            : null,
        dateFrom:
          this.searchParams.from === null
            ? null
            : dateUtils.dateToLocalDate(this.searchParams.from),
        dateTo:
          this.searchParams.to === null ? null : dateUtils.dateToLocalDate(this.searchParams.to),
      })
      .then((data) => {
        this.resetViewState();

        if (data === null) {
          // this.row = loadable.reject("No more pending caregivers");
          this.aiReviewManualCheck = loadable.reject("No caregiver documents to review");
          return;
        }

        this.aiReviewManualCheck = loadable.resolve(data.caregiverDocuments.slice(0, 50));

        // this.refreshCollapsedState();

        this.dropdownDocumentOptions = data.documentTypes.map((documentType) => ({
          id: documentType.id,
          label: documentType.name,
        }));
        data.documentTypes.forEach((documentType) => {
          this.documentTypeIdToName.set(documentType.id, documentType.name);
        });
        this.aiReviewManualCheck.value.forEach((caregiver) => {
          caregiver.documents.forEach((document) => {
            const map = this.documentsState.get(document.caregiverDocumentUploadId) ?? new Map();
            this.documentsState.set(document.caregiverDocumentUploadId, map);
            this.swapDocumentType.set(document.caregiverDocumentUploadId, {
              isTrue: false,
              model: [],
            });

            data.documentTypes.forEach((documentType) => {
              map.set(documentType.id, {
                isCompliant: false,
                effectiveDate: new Date(),
                expiryDate: null,
                fields: document.fields.map((field) => {
                  return match(field)
                    .with({ type: "Text" }, (field) => ({ ...field, model: null }))
                    .with({ type: "Dropdown" }, (field) => ({ ...field, model: null }))
                    .with({ type: "Date" }, (field) => ({ ...field, model: { value: null } }))
                    .exhaustive();
                }),
                expires: document.expires,
                requireReVerification: document.requireReVerification,
                files: null,
                ignoreFiles: true,
                expiryDateButtons: [
                  {
                    title: "1 week",
                    active: false,
                    date: dateUtils.localDateToDate(LocalDate.now().plusWeeks(1)),
                  },
                  {
                    title: "1 month",
                    active: false,
                    date: dateUtils.localDateToDate(LocalDate.now().plusMonths(1)),
                  },
                  {
                    title: "1 year",
                    active: false,
                    date: dateUtils.localDateToDate(LocalDate.now().plusYears(1)),
                  },
                  {
                    title: "Custom date",
                    active: false,
                    date: new Date(),
                  },
                ],
              });
            });
          });
        });
      })
      .catch((error) => {
        console.error(error);
        this.promptErrorModal(error);
        this.aiReviewManualCheck = apiErrors.format(error, "Something went wrong");
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private async fetchAgencyDocumentTypes() {
    this.compService
      .getAgencyDocumentTypes()
      .then((res) => {
        this.documentOptions = res.documentTypes.map((documentType) => ({
          id: documentType.id,
          label: documentType.name,
        }));
      })
      .catch((error) => {
        console.error(error);
        this.promptErrorModal(error);
      });
  }

  // private refreshCollapsedState() {
  //   this.collapseState = {};
  //   this.collapseStateDocuments = {};

  //   if (loadable.isResolved(this.aiReviewManualCheck)) {
  //     this.aiReviewManualCheck.value.forEach((item) => {
  //       this.collapseState[CaregiverId.unwrap(item.caregiver.id)] = { isOpened: true };
  //       item.documents.forEach((document) => {
  //         this.collapseStateDocuments[CaregiverDocumentUploadId.unwrap(document.caregiverDocumentUploadId)] = { isOpened: true };
  //       });
  //     });
  //   }
  // }

  private fetchAgencyCertifications = () => {
    this.agencyCertificationsOptions = this.DatabaseApi.activeAgencyCertifications().map((val) => ({
      id: val.certification,
      label: val.certification,
    }));
  };

  private DEFAULT_COLLAPSE_STATE: boolean = true;

  isCaregiverOpen = (caregiverId: CaregiverId) => {
    return this.collapseState.get(caregiverId) ?? this.DEFAULT_COLLAPSE_STATE;
  };

  toggleCaregiverOpen = (caregiverId: CaregiverId) => {
    this.collapseState.set(
      caregiverId,
      !(this.collapseState.get(caregiverId) ?? this.DEFAULT_COLLAPSE_STATE)
    );
  };

  isDocumentOpen = (documentId: CaregiverDocumentUploadId) => {
    return this.collapseStateDocuments.get(documentId) ?? this.DEFAULT_COLLAPSE_STATE;
  };

  toggleDocumentOpen = (documentId: CaregiverDocumentUploadId) => {
    this.collapseStateDocuments.set(
      documentId,
      !(this.collapseStateDocuments.get(documentId) ?? this.DEFAULT_COLLAPSE_STATE)
    );
  };

  toggleEditMode = () => (this.isEditMode = !this.isEditMode);
}

export const AiReviewManualCheckComponent: ComponentOptions = {
  $name: "aiReviewManualCheckScreen",
  templateUrl:
    "admin/modules/compliance/components/ai-review-manual-check/ai-review-manual-check.component.html",
  controller: Controller,
  controllerAs: "ctrl",
};
